import { useSyncExternalStore } from 'react';

/**
 * The app's address, owned by React Router (`renderer-entry.tsx`) in every host: browsers, the
 * Desktop's window (the local server's origin) and the preview. Code outside the route tree
 * navigates with {@link navigateApp} and reads the address with {@link useAppPath}; without a
 * router (unit tests), they work on the history directly.
 */
const NAVIGATION_EVENT = 'crowdex:navigation';

/** What the store needs of the app's router (`createBrowserRouter`'s). */
export type AppRouter = {
  navigate(to: string, options: { replace: boolean }): unknown;
  subscribe(listener: () => void): () => void;
};

let appRouter: AppRouter | null = null;

/** The router that owns the address from now on. */
export function installAppRouter(router: AppRouter): void {
  appRouter = router;
}

/** The app path in the address now (not the one rendered last). */
export function currentAppPath(): string {
  if (typeof window === 'undefined') return '/desktop';
  return `${window.location.pathname}${window.location.search}`;
}

/** An app full screen, outside any conversation (`/apps/<plugin>[/<route>]`). */
const APP_HOME_PAGE = /^\/apps\/[^/]+(?:\/.*)?$/;

/**
 * Library pages (Settings with its Plugins and Connections, artifacts, an app's home) open over
 * the mounted workspace.
 */
export function isLibraryPagePath(pathname: string): boolean {
  return pathname === '/artifacts' || pathname === '/settings' || APP_HOME_PAGE.test(pathname);
}

const pathnameOf = (path: string): string => new URL(path, 'https://crowdex.invalid').pathname;

/** Where a library page goes back to: the app path it was opened from (this tab only). */
const LIBRARY_RETURN_KEY = 'crowdex:library-return-path';

/** The exact app path (its open conversation included) the library page was opened from. */
export function libraryReturnPath(): string | null {
  try {
    return window.sessionStorage.getItem(LIBRARY_RETURN_KEY);
  } catch {
    return null;
  }
}

function rememberLibraryReturn(from: string, target: string, replace: boolean): void {
  const fromPath = pathnameOf(from);
  if (isLibraryPagePath(fromPath) || !isLibraryPagePath(pathnameOf(target))) return;
  // Replaced, the address stood for the page (the preview's, a sign-in): back to the start.
  const back = !replace
    ? from
    : fromPath.startsWith('/desktop-preview')
      ? '/desktop-preview'
      : '/desktop';
  try {
    window.sessionStorage.setItem(LIBRARY_RETURN_KEY, back);
  } catch {
    // Without storage, leaving falls back to the app's start.
  }
}

export function navigateApp(href: string, replace = false): void {
  const target = href.startsWith('/') ? href : `/${href}`;
  rememberLibraryReturn(currentAppPath(), target, replace);
  if (appRouter !== null) void appRouter.navigate(target, { replace });
  else if (replace) window.history.replaceState(null, '', target);
  else window.history.pushState(null, '', target);
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}

function subscribe(listener: () => void): () => void {
  const unsubscribe = appRouter?.subscribe(listener);
  window.addEventListener('popstate', listener);
  window.addEventListener(NAVIGATION_EVENT, listener);
  return () => {
    unsubscribe?.();
    window.removeEventListener('popstate', listener);
    window.removeEventListener(NAVIGATION_EVENT, listener);
  };
}

export function useAppPath(): string {
  return useSyncExternalStore(subscribe, currentAppPath, () => '/desktop');
}
